import { useMemo, useState, type FormEvent } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Bell, Check, Plus, Search, Trash2, X } from 'lucide-react';
import { matchesWatchRule, type WatchAlert, type WatchRule, type WatchRuleKind } from '../lib/watchlist.mjs';
import { timeAgo } from '../lib/api';
import { type InterfaceLanguage, uiText } from '../lib/i18n';
import type { Article } from '../types';
import { acceptEnglishInput, containsDevanagari, englishOnlyText } from '../lib/englishOnly.mjs';

interface Props {
  language: InterfaceLanguage;
  articles: Article[];
  rules: WatchRule[];
  alerts: WatchAlert[];
  onAddRule: (kind: WatchRuleKind, value: string) => void;
  onToggleRule: (id: string) => void;
  onRemoveRule: (id: string) => void;
  onClearAlerts: () => void;
  onSelectArticle: (article: Article) => void;
  onClose: () => void;
}

const RULE_KINDS: WatchRuleKind[] = ['keyword', 'country', 'publisher'];

export default function WatchlistPanel({ language, articles, rules, alerts, onAddRule, onToggleRule, onRemoveRule, onClearAlerts, onSelectArticle, onClose }: Props) {
  const t = (text: string) => uiText(language, text);
  const prefersReducedMotion = useReducedMotion();
  const dateLanguage = language === 'hi' ? 'hi' : 'en';
  const [kind, setKind] = useState<WatchRuleKind>('keyword');
  const [value, setValue] = useState('');
  const articleById = useMemo(() => new Map(articles.map((article) => [article.id, article])), [articles]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cleanValue = value.trim();
    if (!cleanValue) return;
    onAddRule(kind, cleanValue);
    setValue('');
  };
  const labelForKind = (ruleKind: WatchRuleKind) => t(ruleKind === 'keyword' ? 'कीवर्ड / विषय' : ruleKind === 'country' ? 'देश' : 'प्रकाशक');
  const countFor = (rule: WatchRule) => articles.reduce((count, article) => count + (matchesWatchRule(article, rule) ? 1 : 0), 0);

  return (
    <motion.section className="watchlist-popover" role="dialog" aria-modal="false" aria-label={t('Watchlist और alerts')} initial={prefersReducedMotion ? false : { opacity: 0, y: -7, scale: 0.985 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={prefersReducedMotion ? undefined : { opacity: 0, y: -4 }} transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}>
      <header className="watchlist-heading"><div><span className="watchlist-icon"><Bell size={15} /></span><div><strong>{t('Watchlist और alerts')}</strong><small>{t('इस browser में सुरक्षित')}</small></div></div><button className="watchlist-close" type="button" onClick={onClose} aria-label={t('बंद करें')}><X size={16} /></button></header>
      <form className="watchlist-form" onSubmit={submit}>
        <label htmlFor="watch-rule-type">{t('नियम का प्रकार')}</label>
        <div className="watchlist-form-row">
          <select id="watch-rule-type" value={kind} onChange={(event) => setKind(event.target.value as WatchRuleKind)} aria-label={t('नियम का प्रकार')}>
            {RULE_KINDS.map((ruleKind) => <option key={ruleKind} value={ruleKind}>{labelForKind(ruleKind)}</option>)}
          </select>
          <input value={value} onChange={(event) => { const safe = acceptEnglishInput(event.target.value); if (safe !== null) setValue(safe); }} placeholder={t('शब्द, देश या publisher लिखें')} aria-label={t('शब्द, देश या publisher लिखें')} maxLength={80} />
          <button type="submit" disabled={!value.trim()} aria-label={t('Watch rule जोड़ें')} title={t('Watch rule जोड़ें')}><Plus size={15} /></button>
        </div>
      </form>

      <div className="watchlist-section-heading"><strong>{t('निगरानी नियम')}</strong><span>{rules.length}</span></div>
      {rules.length ? <div className="watch-rule-list">{rules.map((rule) => { const safeRule = englishOnlyText(rule.value, 'Non-English rule hidden'); return <div className={`watch-rule ${rule.enabled ? '' : 'watch-rule-disabled'}`} key={rule.id}>
        <button className="watch-rule-toggle" type="button" onClick={() => onToggleRule(rule.id)} aria-pressed={rule.enabled} aria-label={`${rule.enabled ? t('अक्षम करें') : t('सक्षम करें')}: ${safeRule}`}>{rule.enabled ? <Check size={12} /> : <span />}</button>
        <span className="watch-rule-kind">{labelForKind(rule.kind)}</span><strong title={safeRule}>{safeRule}</strong><small>{countFor(rule)} {t('मिलती रिपोर्ट')}</small>
        <button className="watch-rule-remove" type="button" onClick={() => onRemoveRule(rule.id)} aria-label={`${t('हटाएँ')}: ${safeRule}`} title={t('हटाएँ')}><Trash2 size={13} /></button>
      </div>; })}</div> : <p className="watchlist-empty">{t('अभी watch rules नहीं हैं')}</p>}

      <div className="watchlist-section-heading alerts-heading"><strong>{t('अलर्ट इतिहास')}</strong><span>{alerts.length}</span>{alerts.length > 0 && <button type="button" onClick={onClearAlerts}>{t('अलर्ट साफ़ करें')}</button>}</div>
      {alerts.length ? <div className="watch-alert-list">{alerts.slice(0, 8).map((alert) => {
        const storedArticle = articleById.get(alert.articleId);
        const article = storedArticle && !containsDevanagari(`${storedArticle.title} ${storedArticle.summary} ${storedArticle.sourceName}`) ? storedArticle : undefined;
        return <button className="watch-alert" type="button" key={alert.id} onClick={() => { if (article) onSelectArticle(article); }} disabled={!article}>
          <span className="watch-alert-top"><b>{englishOnlyText(alert.matchLabel, 'Non-English match hidden')}</b><time>{timeAgo(alert.createdAt, dateLanguage)}</time></span>
          <strong>{englishOnlyText(article?.title || alert.title, 'Non-English headline hidden')}</strong><small>{englishOnlyText(article?.sourceName || alert.sourceName, 'Publisher name hidden')} · {t('मिलान हुआ')}</small>
        </button>;
      })}</div> : <p className="watchlist-empty">{t('अभी कोई alert नहीं')}</p>}
      <p className="watchlist-footnote">{t('अलर्ट नई reports पर तभी बनते हैं जब dashboard खुला हो।')}</p>
    </motion.section>
  );
}
